import { useState } from "react";
import { Alert, Button, Card, Descriptions, Empty, Input, Modal, Select, Progress, Space, Tag, Timeline, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SECS, type ActionDto, type AuditDetailDto, type NcDto, type QuestionDto } from "@audit-plan/shared";
import { api } from "../../api/client";
import { invalidateAudit } from "../../api/audits";
import { usePeople } from "../../api/people";
import { useAuth } from "../../auth/AuthContext";
import { palette } from "../../theme";
import { ActionStatusPill, AuditStatusPill, DueLabel, Pill, PriorityPill, Rating, SeverityPill, fmtD, fmtDT, fmtN } from "../pills";
import { NcFormModal } from "../nc/NcFormModal";
import { ActionFormModal } from "../action/ActionFormModal";
import { EvidencePanel } from "../evidence/EvidencePanel";
import { useFullModal } from "./mobileModal";
import { UploadModal } from "../evidence/UploadModal";
import { useNotify } from "./useNotify";
import { AuditFormModal } from "./AuditFormModal";
import { ResizableTable } from "../ResizableTable";
import { Sensitive } from "../Sensitive";

const capPct = (acts: ActionDto[]) => (acts.length ? Math.round((acts.filter((a) => a.status === "done" || a.status === "closed").length / acts.length) * 100) : null);
const mono = (c: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{c}</span>;
const ncStatus = (n: NcDto, t: (k: string) => string) => <Pill tone={n.status === "closed" ? "ok" : "bad"}>{n.status === "closed" ? t("closed") : t("open")}</Pill>;

/* ------------------------------ Non conformità ------------------------------ */
export function NcTab({ audit, questions, onOpenNc }: { audit: AuditDetailDto; questions: QuestionDto[]; onOpenNc: (id: string) => void }) {
  const { t } = useTranslation();
  const people = usePeople();
  const [open, setOpen] = useState(false);
  const ed = audit.allowed.ncEdit && audit.status !== "planned" && audit.outcome !== "phaseout";
  return (
    <Card title={t("ncs")} extra={ed ? <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setOpen(true)}>{t("newNc")}</Button> : null}>
      <ResizableTable<NcDto>
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ x: "max-content" }}
        dataSource={[...audit.ncs].sort((a, b) => a.code.localeCompare(b.code))}
        locale={{ emptyText: t("none") }}
        onRow={(n) => ({ onClick: () => onOpenNc(n.id), style: { cursor: "pointer" } })}
        columns={[
          { title: t("code"), dataIndex: "code", render: mono },
          { title: t("severity"), render: (_: unknown, n) => <SeverityPill s={n.severity} /> },
          { title: t("title"), render: (_: unknown, n) => <div>{n.title}<div style={{ fontSize: 12, color: palette.muted }}>{(n.description || "").slice(0, 120)}</div></div> },
          {
            title: t("area"),
            render: (_: unknown, n) => {
              const q = questions.find((x) => x.id === n.questionId);
              return q ? <div style={{ fontSize: 12 }}>{q.code}<div style={{ color: palette.muted }}>{q.area}</div></div> : t("manual");
            },
          },
          { title: t("owner"), dataIndex: "ownerId", render: (id: string) => people.name(id) },
          {
            title: t("cap"),
            align: "right" as const,
            render: (_: unknown, n) => {
              const al = audit.actions.filter((a) => a.ncId === n.id);
              return al.length ? `${capPct(al)}%` : "—";
            },
          },
          { title: t("status"), render: (_: unknown, n) => ncStatus(n, t) },
        ]}
      />
      <NcFormModal open={open} onClose={() => setOpen(false)} auditId={audit.id} />
    </Card>
  );
}

/* ------------------------------ Osservazioni ------------------------------ */
export function ObsTab({ audit, questions }: { audit: AuditDetailDto; questions: QuestionDto[] }) {
  const { t, i18n } = useTranslation();
  const en = i18n.language === "en";
  const qc = useQueryClient();
  const n = useNotify();
  const people = usePeople();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const fm = useFullModal();
  const [section, setSection] = useState<string | undefined>();
  const ed = audit.allowed.ncEdit && ["in_progress", "completed", "in_validation"].includes(audit.status);
  const add = useMutation({
    mutationFn: () => api.post(`/audits/${audit.id}/observations`, { text: text.trim(), section: section ?? null }),
    onSuccess: () => {
      invalidateAudit(qc, audit.id);
      setOpen(false);
      setText("");
      setSection(undefined);
    },
    onError: n.fail,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/audits/${audit.id}/observations/${id}`),
    onSuccess: () => invalidateAudit(qc, audit.id),
    onError: n.fail,
  });
  return (
    <div>
      <Card title={t("observations")} extra={ed ? <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setOpen(true)}>{t("addObs")}</Button> : null}>
        {audit.observations.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("none")} />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size={10}>
            {audit.observations.map((o) => {
              const q = questions.find((x) => x.id === o.questionId);
              return (
                <div key={o.id} style={{ padding: "8px 12px", border: `1px solid ${palette.hair}`, borderRadius: 8, display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    {q ? <div style={{ fontSize: 12 }}><b>{q.code}</b> {en ? q.en || q.it : q.it}</div> : null}
                    <div style={{ whiteSpace: "pre-wrap" }}>{o.text}</div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {people.name(o.byId)} · {fmtDT(o.createdAt)}
                      {o.section ? ` · ${t(`sec_${o.section}`)}` : ""}
                      {q ? ` · ${en ? "from checklist (OSS)" : "dalla checklist (OSS)"}` : ""}
                    </Typography.Text>
                  </div>
                  {ed && !q ? (
                    <Button size="small" danger type="text" onClick={() => del.mutate(o.id)}>
                      {t("delete")}
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </Space>
        )}
      </Card>
      <Typography.Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
        {en ? "An OSS answer in the checklist opens an observation, not a non-conformity, and keeps the question's points." : "La risposta OSS nella checklist apre un'osservazione, non una non conformità, e mantiene i punti della domanda."}
      </Typography.Paragraph>
      <Modal {...fm} open={open} title={t("addObs")} onCancel={() => setOpen(false)} okText={t("save")} cancelText={t("cancel")} okButtonProps={{ disabled: !text.trim() }} confirmLoading={add.isPending} onOk={() => add.mutate()} destroyOnHidden>
        <Typography.Text>{t("obsText")} *</Typography.Text>
        <Input.TextArea rows={4} value={text} onChange={(e) => setText(e.target.value)} />
        <div style={{ marginTop: 12 }}>
          <Typography.Text>{t("section")}</Typography.Text>
          <Select style={{ width: "100%" }} allowClear value={section} onChange={setSection} options={SECS.map((s) => ({ value: s, label: t(`sec_${s}`) }))} placeholder="—" />
        </div>
      </Modal>
    </div>
  );
}

/* ------------------------------ CAP / Follow-up ------------------------------ */
export function CapTab({ audit, questions, followUps, onOpenAction }: { audit: AuditDetailDto; questions: QuestionDto[]; followUps: { id: string; code: string; status: AuditDetailDto["status"]; outcome: AuditDetailDto["outcome"] }[]; onOpenAction: (id: string) => void }) {
  const { t, i18n } = useTranslation();
  const en = i18n.language === "en";
  const { can } = useAuth();
  const people = usePeople();
  const qc = useQueryClient();
  const n = useNotify();
  const [actFor, setActFor] = useState<NcDto | null>(null);
  const [followUp, setFollowUp] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [capUp, setCapUp] = useState(false);
  const fm = useFullModal();

  const acts = audit.actions;
  const cp = capPct(acts);
  const cs = audit.capStatus ?? "open";
  const openNc = audit.ncs.filter((x) => x.status !== "closed").length;
  const post = ["completed", "in_validation", "closed"].includes(audit.status);
  const rev = audit.allowed.cap && post && audit.ncs.length > 0;
  const ed = audit.allowed.ncEdit && post;
  const capDocs = audit.evidence.filter((e) => e.target.type === "cap");

  const setCap = useMutation({
    mutationFn: (to: "open" | "in_progress" | "closed") => api.post(`/audits/${audit.id}/cap`, { to }),
    onSuccess: () => {
      invalidateAudit(qc, audit.id);
      setConfirmClose(false);
    },
    onError: n.fail,
  });

  if (audit.outcome === "phaseout")
    return <Alert type="error" message={en ? "Phase-out outcome (zero tolerance): no Corrective Action Plan is opened." : "Esito phase-out (zero tolerance): non si apre nessun Corrective Action Plan."} />;

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <Typography.Text type="secondary" style={{ textTransform: "uppercase", fontSize: 12 }}>{t("overallCap")}</Typography.Text>
            <Space align="center" size={12} wrap>
              <span style={{ fontSize: 26, fontWeight: 800 }}>{cp == null ? "—" : `${cp}%`}</span>
              <Typography.Text type="secondary">{acts.filter((a) => a.status === "done" || a.status === "closed").length}/{acts.length}</Typography.Text>
              <Pill tone={cs === "closed" ? "ok" : cs === "in_progress" ? "info" : "warn"}>{`${t("capStatus")}: ${t(`cap_${cs}`)}`}</Pill>
              {openNc ? <Typography.Text type="secondary">{openNc} NC {en ? "open" : "aperte"}</Typography.Text> : null}
            </Space>
          </div>
          <Space wrap align="start">
            {capDocs.length ? <EvidenceInline docs={capDocs.map((d) => d.name)} /> : null}
            {audit.allowed.upload ? <Button size="small" onClick={() => setCapUp(true)}>{t("capDoc")}</Button> : null}
            {rev && cs === "open" ? <Button size="small" onClick={() => setCap.mutate("in_progress")}>{en ? "Put CAP under review" : "Metti CAP in verifica"}</Button> : null}
            {rev && cs !== "closed" ? <Button size="small" type="primary" onClick={() => (openNc ? setConfirmClose(true) : setCap.mutate("closed"))}>{en ? "Close CAP" : "Chiudi CAP"}</Button> : null}
            {rev && cs === "closed" ? <Button size="small" onClick={() => setCap.mutate("open")}>{en ? "Reopen CAP" : "Riapri CAP"}</Button> : null}
            {can("audit.create") && audit.status === "closed" ? <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setFollowUp(true)}>{t("newFollowup")}</Button> : null}
          </Space>
        </div>
        <Progress percent={cp ?? 0} showInfo={false} strokeColor={palette.ok} style={{ marginTop: 10 }} />
        {followUps.length ? (
          <div style={{ marginTop: 8, fontSize: 13 }}>
            {en ? "Follow-up audits" : "Audit di follow-up"}:{" "}
            {followUps.map((f) => (
              <span key={f.id} style={{ marginRight: 10 }}>
                <Link to={`/audits/${f.id}`}>{f.code}</Link> <AuditStatusPill a={f} />
              </span>
            ))}
          </div>
        ) : null}
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
          {en ? "The CAP lists every NC found in the audit. It can be put under review or closed even with open NCs; the supplier's owner closes each action." : "Il CAP elenca tutte le NC rilevate nell'audit. Si può mettere in verifica o chiudere anche con NC aperte; ogni azione la chiude il responsabile del fornitore."}
        </Typography.Paragraph>
      </Card>
      {!post ? <Alert type="info" showIcon style={{ marginBottom: 16 }} message={en ? "Actions are defined after the audit is completed." : "Le azioni si definiscono dopo il completamento dell'audit."} /> : null}
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {audit.ncs.length === 0 ? <Card><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={en ? "No non-conformities: no CAP needed." : "Nessuna non conformità: CAP non necessario."} /></Card> : null}
        {audit.ncs.map((nc) => {
          const al = acts.filter((x) => x.ncId === nc.id);
          const q = questions.find((x) => x.id === nc.questionId);
          return (
            <Card
              key={nc.id}
              title={
                <Space wrap>
                  {mono(nc.code)}
                  <SeverityPill s={nc.severity} />
                  {q ? <Typography.Text type="secondary">{q.code}</Typography.Text> : null}
                  <b>{nc.title}</b>
                </Space>
              }
              extra={ed && nc.status !== "closed" && cs !== "closed" ? <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setActFor(nc)}>{t("newAction")}</Button> : null}
            >
              <Typography.Paragraph style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
                <Typography.Text type="secondary">{en ? "Finding" : "Rilievo"}:</Typography.Text> {nc.description}
              </Typography.Paragraph>
              <ResizableTable<ActionDto>
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ x: "max-content" }}
                dataSource={al}
                locale={{ emptyText: en ? "No actions for this NC" : "Nessuna azione per questa NC" }}
                onRow={(a) => ({ onClick: () => onOpenAction(a.id), style: { cursor: "pointer" } })}
                columns={[
                  { title: t("code"), dataIndex: "code", render: mono },
                  { title: t("title"), dataIndex: "title" },
                  { title: t("assignee"), render: (_: unknown, a) => <Sensitive>{a.assigneeId ? people.name(a.assigneeId) : a.assigneeName || "—"}</Sensitive> },
                  { title: t("priority"), render: (_: unknown, a) => <PriorityPill p={a.priority} /> },
                  { title: t("dueDate"), render: (_: unknown, a) => <DueLabel a={a} /> },
                  { title: t("progress"), render: (_: unknown, a) => <Progress percent={a.progress} size="small" style={{ width: 90 }} strokeColor={palette.ok} /> },
                  { title: t("status"), render: (_: unknown, a) => <ActionStatusPill a={a} /> },
                ]}
              />
            </Card>
          );
        })}
      </Space>
      {actFor ? <ActionFormModal open onClose={() => setActFor(null)} nc={actFor} /> : null}
      <UploadModal open={capUp} onClose={() => setCapUp(false)} ctx={{ supplierId: audit.supplierId, auditId: audit.id, target: { type: "cap", id: audit.id } }} />
      <AuditFormModal open={followUp} onClose={() => setFollowUp(false)} parent={audit} />
      <Modal {...fm} open={confirmClose} onCancel={() => setConfirmClose(false)} title={t("confirm")} okText={t("yes")} cancelText={t("cancel")} confirmLoading={setCap.isPending} onOk={() => setCap.mutate("closed")}>
        {en ? `${openNc} NCs are still open. Close the CAP anyway?` : `Ci sono ancora ${openNc} NC aperte. Chiudere comunque il CAP?`}
      </Modal>
    </div>
  );
}

function EvidenceInline({ docs }: { docs: string[] }) {
  return (
    <>
      {docs.map((d) => (
        <Tag key={d}>{d}</Tag>
      ))}
    </>
  );
}

/* ------------------------------ Report ------------------------------ */
export function ReportTab({ audit, scores, override, reason, onPdf }: { audit: AuditDetailDto; scores: { S: number | null; H: number | null; E: number | null; total: number | null }; override?: string | null; reason?: string | null; onPdf: () => void }) {
  const { t } = useTranslation();
  const people = usePeople();
  const published = audit.status === "closed";
  const openActs = audit.actions.filter((a) => a.status !== "done" && a.status !== "closed").length;
  return (
    <Card
      title={published ? t("publishedReport") : `${t("report")} (${t("preliminary")})`}
      extra={<Button type="primary" onClick={onPdf}>{t("auditReport")}</Button>}
    >
      {published ? null : <Alert type="info" showIcon message={t("reportNotYet")} style={{ marginBottom: 16 }} />}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 24 }}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t("scoreTotal")}>
            {scores.total == null ? "—" : fmtN(scores.total, 1)} <Rating score={scores.total} override={override} reason={reason} />{override === "D" && reason ? <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>{reason}</Typography.Text> : null}
          </Descriptions.Item>
          {SECS.map((k) => (
            <Descriptions.Item key={k} label={t(`sec_${k}`)}>
              {scores[k] == null ? "—" : fmtN(scores[k], 1)}
            </Descriptions.Item>
          ))}
        </Descriptions>
        <Descriptions column={1} size="small">
          <Descriptions.Item label={t("evidence")}>{audit.evidence.length}</Descriptions.Item>
          <Descriptions.Item label={t("ncs")}>
            {audit.ncs.length} ({audit.ncs.filter((x) => x.status !== "closed").length} {t("open").toLowerCase()})
          </Descriptions.Item>
          <Descriptions.Item label={t("openActions")}>{openActs}</Descriptions.Item>
          <Descriptions.Item label={t("observations")}>{audit.observations.length}</Descriptions.Item>
          {audit.validatedById ? (
            <Descriptions.Item label={t("b_validated_by")}>
              {people.name(audit.validatedById)} · {fmtD(audit.closedAt)}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </div>
    </Card>
  );
}

/* ------------------------------ Evidenze ------------------------------ */
export function AuditEvidenceTab({ audit, onChanged }: { audit: AuditDetailDto; onChanged?: () => void }) {
  const evs = [...audit.evidence].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  return <EvidencePanel evidence={evs} ctx={{ supplierId: audit.supplierId, auditId: audit.id, target: { type: "audit", id: audit.id } }} canUpload={audit.allowed.upload} onChanged={onChanged} />;
}

/* ------------------------------ Cronologia ------------------------------ */
export function HistoryTab({ audit }: { audit: AuditDetailDto }) {
  const { t } = useTranslation();
  const people = usePeople();
  return (
    <Card>
      {audit.history.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("none")} />
      ) : (
        <Timeline
          items={[...audit.history].reverse().map((h) => ({
            children: (
              <div>
                <div>
                  <b>{people.show(h.by)}</b> · {h.from ? `${t(`st_${h.from}`)} → ` : ""}
                  <b>{h.to ? t(`st_${h.to}`) : ""}</b>
                </div>
                {h.note ? <Typography.Text type="secondary">{h.note}</Typography.Text> : null}
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {fmtDT(h.ts)}
                  </Typography.Text>
                </div>
              </div>
            ),
          }))}
        />
      )}
      {audit.capHistory.length ? (
        <>
          <Typography.Title level={5}>{t("capStatus")}</Typography.Title>
          <Timeline
            items={[...audit.capHistory].reverse().map((h) => ({
              children: (
                <div>
                  <b>{people.show(h.by)}</b> · {t(`cap_${h.to}`)}
                  {h.openNc ? ` (${h.openNc} NC)` : ""} · <Typography.Text type="secondary">{fmtDT(h.ts)}</Typography.Text>
                </div>
              ),
            }))}
          />
        </>
      ) : null}
    </Card>
  );
}

